import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { useToast } from '../../lib/toast';
import { exportName } from './logik';
import type { MonatAntwort } from './types';

function speichern(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// Exportleiste: „CSV für ADDISON“ (7 Spalten) und „DATEV-Format“ (Buchungsstapel). Aktiv, wenn der Server den Export
// erlaubt (Bankkonten bestätigt); sonst steht genau sein Satz daneben. Ist eine Ampel rot (Differenz, offene Zeilen),
// fragt ein Klick erst nach („Trotzdem exportieren“); hinaus gehen nur bestätigte Zeilen. Das DATEV-Format braucht
// zusätzlich Berater- und Mandantennummer (Karte darunter, nur wenn sie fehlen).
export function ExportLeiste({ d, isAdmin, onNeu }: { d: MonatAntwort; isAdmin: boolean; onNeu(): Promise<void> }) {
  const [busy, setBusy] = useState<'addison' | 'extf' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [frage, setFrage] = useState<'addison' | 'extf' | null>(null);
  const datevAus = d.datev.fehlt;
  const warnung = d.export.moeglich ? d.export.warnung : null;
  const klick = (art: 'addison' | 'extf') => (warnung ? setFrage(art) : void laden(art));

  const laden = async (art: 'addison' | 'extf') => {
    setFrage(null);
    setBusy(art);
    setFehler(null);
    try {
      const blob = await api.file(`/api/kanzlei/buchen/${d.mandant.id}/${d.jahr}/${d.monat}/export?art=${art}`);
      speichern(blob, exportName(art, d.jahr, d.monat));
    } catch (err) {
      // 409: der Satz vom Server, warum es noch nicht geht
      setFehler(errorText(err));
      void onNeu();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card ex" aria-labelledby="ex-h">
      <div className="ch">
        <h3 id="ex-h">Export</h3>
        <span>Datei nicht in Excel öffnen und speichern.</span>
      </div>
      <div className="ex-r">
        <button type="button" className="btn dark" disabled={!d.export.moeglich || busy !== null} onClick={() => klick('addison')}>
          {busy === 'addison' ? 'Lädt …' : 'CSV für ADDISON'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!d.export.moeglich || datevAus || busy !== null}
          onClick={() => klick('extf')}
        >
          {busy === 'extf' ? 'Lädt …' : 'DATEV-Format'}
        </button>
        {!d.export.moeglich && d.export.grund && <span className="ex-grund">{d.export.grund}</span>}
        {warnung && !frage && <span className="ex-grund">{warnung} Export trotzdem möglich, nur bestätigte Zeilen.</span>}
        {d.export.moeglich && !warnung && datevAus && <span className="ex-grund">Für das DATEV-Format fehlen noch Nummern.</span>}
      </div>
      {frage && warnung && (
        <div className="ex-frage" role="alert">
          <p>
            {warnung} Trotzdem exportieren? In der Datei stehen nur die bestätigten Zeilen.
          </p>
          <div className="ex-r">
            <button type="button" className="btn dark" onClick={() => void laden(frage)}>
              Trotzdem exportieren
            </button>
            <button type="button" className="btn" onClick={() => setFrage(null)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
      {fehler && (
        <p className="zd-err" role="alert">
          {fehler}
        </p>
      )}
      {datevAus && <DatevKarte d={d} isAdmin={isAdmin} onNeu={onNeu} />}
    </section>
  );
}

// Fehlende DATEV-Nummern: Mandantennummer, Beraternummer (nur Admin, gilt für die ganze Kanzlei), Sachkontenlänge
function DatevKarte({ d, isAdmin, onNeu }: { d: MonatAntwort; isAdmin: boolean; onNeu(): Promise<void> }) {
  const toast = useToast();
  const [mandant, setMandant] = useState(d.datev.mandant ? String(d.datev.mandant) : '');
  const [berater, setBerater] = useState('');
  const [laenge, setLaenge] = useState(d.datev.laenge || 4);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const beraterFehlt = d.datev.berater == null;
  // ohne Admin-Recht und mit Mandantennummer bleibt nur der Satz
  const nurSatz = beraterFehlt && !isAdmin && d.datev.mandant != null;

  const senden = async (e: FormEvent) => {
    e.preventDefault();
    const m = mandant.trim();
    const b = berater.trim();
    if (!/^\d{1,5}$/.test(m) || Number(m) < 1) return setFehler('Mandantennummer: 1 bis 5 Ziffern.');
    if (beraterFehlt && isAdmin && b && (!/^\d{4,7}$/.test(b) || Number(b) < 1001)) {
      return setFehler('Beraternummer: 4 bis 7 Ziffern, ab 1001.');
    }
    setBusy(true);
    setFehler(null);
    try {
      await api.put(`/api/kanzlei/buchen/${d.mandant.id}/datev`, {
        mandant: Number(m),
        laenge,
        ...(beraterFehlt && isAdmin && b ? { berater: Number(b) } : {}),
      });
      await onNeu();
      toast('DATEV-Nummern gespeichert');
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (nurSatz) return <p className="ex-datev note">Die Beraternummer fehlt. Sie trägt der Admin ein.</p>;
  return (
    <form className="ex-datev" onSubmit={senden} aria-label="DATEV-Nummern">
      <p className="note">Nur für das DATEV-Format nötig.</p>
      <div className="ex-datev-r">
        <label>
          Mandantennummer
          <input className="num" value={mandant} inputMode="numeric" maxLength={5} required onChange={(e) => setMandant(e.target.value.replace(/\D/g, ''))} />
        </label>
        {beraterFehlt && isAdmin && (
          <label>
            Beraternummer
            <input className="num" value={berater} inputMode="numeric" maxLength={7} onChange={(e) => setBerater(e.target.value.replace(/\D/g, ''))} />
          </label>
        )}
        <label>
          Sachkontenlänge
          <select value={laenge} onChange={(e) => setLaenge(Number(e.target.value))}>
            {[4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn sm dark" disabled={busy}>
          Speichern
        </button>
      </div>
      {beraterFehlt && !isAdmin && <p className="note">Die Beraternummer trägt der Admin ein.</p>}
      {fehler && (
        <p className="zd-err" role="alert">
          {fehler}
        </p>
      )}
    </form>
  );
}
